import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { generatePath } from 'react-router';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
  FOOTBALL_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';

import type { So5LineupNotification_so5LineupNotification } from './__generated__/index.graphql.ts';

type Props = {
  notification: So5LineupNotification_so5LineupNotification;
};

export const So5LineupNotification = ({ notification, ...rest }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();

  const { createdAt, name, so5Lineup, card, sport, read } = notification;
  if (!so5Lineup.so5Leaderboard) return null;
  if (!card) return null;

  const link =
    name === 'so5_lineup_cancelled' || name === 'appearance_destroyed'
      ? generateSportContextPath(
          ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
          {
            params: {
              fixture: so5Lineup.so5Fixture.slug,
              eventType: so5Lineup.so5Fixture.type.toLowerCase(),
              contender: so5Lineup.so5LeaderboardContender.slug,
            },
          }
        )
      : generatePath(
          FOOTBALL_PLAY_EVENTTYPE_COMPOSE_LEADERBOARD_LINEUP_LINEUP,
          {
            leaderboard: so5Lineup.so5Leaderboard.slug,
            lineup: idFromObject(so5Lineup.id),
            eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
          }
        );

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      userAvatar={currentUser}
      link={link}
      createdAt={createdAt}
      sport={sport}
      read={read}
      {...rest}
    />
  );
};

So5LineupNotification.fragments = {
  so5LineupNotification: gql`
    fragment So5LineupNotification_so5LineupNotification on So5LineupNotification {
      id
      ...Notification_notificationInterface
      card: anyCard {
        slug
      }
      so5Lineup {
        id
        so5Fixture {
          slug
          type
        }
        so5LeaderboardContender {
          slug
        }
        so5Leaderboard {
          slug
        }
      }
    }
    ${commonNotificationInterfaceFragment}
  ` as TypedDocumentNode<So5LineupNotification_so5LineupNotification>,
};
